if (!customElements.get('corporate-gifts')) {
  const BASKET_KEY = 'wildgrain-corporate-basket';
  const FILTER_KEY = 'cg-filters';
  const CACHE_TTL_MS = 10 * 60 * 1000;

  class CorporateGifts extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-cg-grid]');
      this.sortSelect = this.querySelector('[data-cg-sort]');
      this.basket = this.querySelector('[data-cg-basket]');
      this.basketItems = this.querySelector('[data-cg-basket-items]');
      this.basketEmpty = this.querySelector('[data-cg-basket-empty]');
      this.basketFooter = this.querySelector('[data-cg-basket-footer]');
      this.basketCount = this.querySelector('[data-cg-basket-count]');
      this.bundleContents = this.querySelector('bundle-contents');
      this.openModal = null;

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      window.addEventListener('pageshow', this.handlePageShow);

      this.renderBasket();
      this.applyInitialSort();
      this.initFilters();
      this.settlePlacedOrder();
      this.initCollages();
    }

    /* Card collages: swap each box's contents photos for the restyled product
       thumbnails (api.wildgrain.com styled-thumbnails, style from
       data-thumb-style, e.g. "minimal"). The box's items come from the same
       bundle endpoint bundle-contents.js uses, keyed by the physical SKU
       (corporate SKUs carry a _CORP suffix). Anything that fails leaves the
       Liquid-rendered photos in place, so this is purely progressive. */
    get thumbStyle() {
      return (this.dataset.thumbStyle || '').trim().toLowerCase();
    }

    get apiBase() {
      return (this.dataset.apiBase || '').replace(/\/$/, '');
    }

    /* handle -> restyled URL (also '#<product id>' keys) for the active style.
       Memoised per element; resolves to null when the style is off or the map
       cannot be fetched, so callers just skip the restyle. */
    styledThumbs() {
      if (this._styledThumbs) return this._styledThumbs;
      const style = this.thumbStyle;
      const apiBase = this.apiBase;
      if (!style || !apiBase) return (this._styledThumbs = Promise.resolve(null));

      this._styledThumbs = this.cachedJson(
        'cg-thumbs-' + style,
        apiBase + '/api/styled-thumbnails/map/' + encodeURIComponent(style)
      )
        .then((map) => {
          const byHandle = {};
          Object.entries((map && map.thumbnails) || {}).forEach(([id, entry]) => {
            if (!entry || !entry.url) return;
            byHandle['#' + id] = entry.url;
            if (entry.handle) byHandle[entry.handle] = entry.url;
          });
          return Object.keys(byHandle).length ? byHandle : null;
        })
        .catch(() => null);
      return this._styledThumbs;
    }

    /* The box's items (bundle endpoint shared with bundle-contents.js), keyed
       by the physical SKU — corporate SKUs carry a _CORP suffix. */
    async bundleItems(rawSku) {
      const sku = (rawSku || '').trim().replace(/_CORP$/i, '');
      if (!sku || !this.apiBase) return [];
      const data = await this.cachedJson(
        'cg-bundle-' + sku,
        this.apiBase + '/api/bundle/storefront/' + encodeURIComponent(sku) + '?box_number=1'
      );
      return (data && data.success && data.items) || [];
    }

    /* Items -> [{ url, title }] for those with a restyled shot, de-duplicated. */
    styledItems(items, byHandle) {
      const out = [];
      items.forEach((item) => {
        const url = byHandle[item.handle] || byHandle['#' + item.product_shopify_id];
        if (url && !out.some((entry) => entry.url === url)) {
          out.push({ url: url, title: item.product_name || item.handle || '' });
        }
      });
      return out;
    }

    async initCollages() {
      if (!this.grid) return;

      // Tiles shimmer until each collage is marked ready (see the CSS); a card
      // whose restyle fails or is skipped reveals its fallback photos instead.
      const ready = (card) => {
        const collage = card.querySelector('[data-cg-collage]');
        if (collage) collage.classList.add('is-ready');
      };

      const cards = Array.from(this.grid.querySelectorAll('[data-cg-card]')).filter((card) => {
        if (!card.querySelector('[data-cg-collage]')) return false;
        if (card.dataset.cgSku) return true;
        ready(card); // nothing to look up for this one
        return false;
      });
      if (!cards.length) return;

      let byHandle = null;
      try {
        byHandle = await this.styledThumbs();
      } catch (e) {
        /* fall through: reveal the photos */
      }
      if (!byHandle) {
        cards.forEach(ready);
        return;
      }

      // A few at a time keeps the page's own image loading ahead of the API.
      const queue = cards.slice();
      const worker = async () => {
        while (queue.length) {
          const card = queue.shift();
          try {
            await this.restyleCollage(card, byHandle);
          } catch (e) {
            /* keep the fallback photos */
          }
          ready(card);
        }
      };
      await Promise.all([worker(), worker(), worker()]);
    }

    /* Row table for the collage grid (mirrors corporate-gift-card.liquid):
       how many tiles sit in each row for a given item count. */
    collageRows(count) {
      const table = {
        1: [1],
        2: [2],
        3: [1, 2],
        4: [2, 2],
        5: [2, 3],
        6: [3, 3],
        7: [2, 3, 2],
        8: [3, 2, 3],
        9: [3, 3, 3],
        10: [3, 4, 3],
        11: [4, 3, 4],
        12: [4, 4, 4],
        13: [3, 4, 3, 3],
        14: [3, 4, 4, 3],
        15: [4, 4, 4, 3],
      };
      return table[Math.min(count, 16)] || [4, 4, 4, 4];
    }

    /* Size the grid and each tile for `tiles.length` items. */
    layoutCollage(collage, tiles) {
      const rows = this.collageRows(tiles.length);
      collage.style.setProperty('--cg-rows', String(rows.length));
      collage.dataset.cgRows = rows.join(',');
      let rowIndex = 0;
      let inRow = 0;
      tiles.forEach((tile) => {
        const size = rows[rowIndex];
        tile.style.setProperty('--cg-span', String(60 / size));
        const stretched = (size === 1 && rows.length > 1) || (rows.length === 1 && size > 1);
        tile.classList.toggle('cg-card__collage-tile--wide', stretched);
        inRow += 1;
        if (inRow >= size) {
          rowIndex += 1;
          inRow = 0;
        }
      });
    }

    async restyleCollage(card, byHandle) {
      const items = await this.bundleItems(card.dataset.cgSku);
      const urls = this.styledItems(items, byHandle).map((entry) => entry.url);
      if (!urls.length) return;

      const collage = card.querySelector('[data-cg-collage]');
      const tiles = Array.from(collage.querySelectorAll('[data-cg-tile]'));
      if (!tiles.length) return;

      // Items without a restyled shot keep a slot, filled from the product's
      // own contents photos, so every item in the box still gets a tile.
      const wanted = Math.min(items.length, 16);
      const fallbacks = tiles
        .map((tile) => tile.querySelector('img'))
        .filter(Boolean)
        .map((img) => img.currentSrc || img.src)
        .filter(Boolean);
      for (let i = 0; urls.length < wanted && i < fallbacks.length; i += 1) {
        if (!urls.includes(fallbacks[i])) urls.push(fallbacks[i]);
      }
      urls.length = Math.min(urls.length, wanted);
      if (!urls.length) return;

      // Grow or shrink to the resolved item count, re-lay the grid, then swap
      // every tile's photo once it has loaded.
      const finalShown = urls.length;
      while (tiles.length < finalShown) {
        const clone = tiles[0].cloneNode(true);
        collage.appendChild(clone);
        tiles.push(clone);
      }
      tiles.slice(finalShown).forEach((tile) => tile.remove());
      tiles.length = finalShown;
      this.layoutCollage(collage, tiles);

      await Promise.all(
        tiles.map((tile, index) => {
          const img = tile.querySelector('img');
          if (!img) return null;
          const src = this.sizedThumb(urls[index], 500);
          if ((img.currentSrc || img.src) === urls[index]) return null;
          return this.preload(src).then(() => {
            img.classList.add('is-swapping');
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = src;
            requestAnimationFrame(() => img.classList.remove('is-swapping'));
          });
        })
      );
    }

    sizedThumb(url, width) {
      try {
        const u = new URL(url);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (e) {
        return url;
      }
    }

    preload(src) {
      return new Promise((resolve) => {
        const probe = new Image();
        probe.onload = resolve;
        probe.onerror = () => resolve();
        probe.src = src;
      });
    }

    /* GET + parse, memoised in sessionStorage so sorting, filtering and back
       navigation never refetch the same bundle twice in a tab. Entries expire
       after ten minutes (the API caches the thumbnail map for the same span)
       so a freshly published thumbnail reaches an open tab on its next load. */
    async cachedJson(key, url) {
      const now = Date.now();
      try {
        const hit = JSON.parse(sessionStorage.getItem(key));
        if (hit && hit.ts && now - hit.ts < CACHE_TTL_MS) return hit.data;
      } catch (e) {
        /* storage unavailable or legacy entry */
      }
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(url + ' ' + res.status);
      const json = await res.json();
      try {
        sessionStorage.setItem(key, JSON.stringify({ ts: now, data: json }));
      } catch (e) {
        /* quota / private mode */
      }
      return json;
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.handlePageShow);
      if (this.updateFilterArrows) window.removeEventListener('resize', this.updateFilterArrows);
    }

    /* Checkout is a cross-origin navigation, so coming back with Cancel often
       restores this page from bfcache. Hidden lazy thumbnails abort mid-load
       and Safari leaves the broken-image icon; rebuild them. */
    handlePageShow(event) {
      if (event.persisted) this.renderBasket();
    }

    /* Coming back to the store after paying, the basket badge would still count
       the boxes that were just ordered. Drop them once the order is past draft
       (see corporate-basket-storage.js). */
    async settlePlacedOrder() {
      const storage = window.WildgrainCorporateBasket;
      if (!storage) return;
      if (await storage.settle(this.dataset.apiBase)) this.renderBasket();
    }

    applyInitialSort() {
      if (!this.sortSelect || !this.grid) return;

      const defaultSort = this.sortSelect.value;
      let initialSort = null;
      try {
        initialSort = new URL(window.location.href).searchParams.get('sort_by');
        if (!initialSort) initialSort = sessionStorage.getItem('cg-sort-by');
      } catch (e) {
        /* ignore */
      }

      if (!initialSort || initialSort === defaultSort) return;
      if (!Array.from(this.sortSelect.options).some((option) => option.value === initialSort)) return;

      this.sortSelect.value = initialSort;
      this.sortCards(initialSort);
    }

    handleClick(event) {
      const opener = event.target.closest('[data-cg-open]');
      if (opener) {
        this.show(opener.dataset.cgOpen, opener);
        return;
      }

      if (event.target.closest('[data-cg-close]')) {
        this.hide();
        return;
      }

      const thumb = event.target.closest('[data-cg-thumb]');
      if (thumb) {
        this.selectThumb(thumb);
        return;
      }

      const addBtn = event.target.closest('[data-cg-add]');
      if (addBtn) {
        this.addToBasket(addBtn);
        return;
      }

      if (event.target.closest('[data-cg-basket-open]')) {
        this.showBasket();
        return;
      }

      if (event.target.closest('[data-cg-basket-close]')) {
        this.hideBasket();
        return;
      }

      const stepBtn = event.target.closest('[data-cg-step]');
      if (stepBtn) {
        this.changeQuantity(stepBtn.dataset.variantId, Number(stepBtn.dataset.cgStep));
        return;
      }

      const removeBtn = event.target.closest('[data-cg-remove]');
      if (removeBtn) {
        this.removeItem(removeBtn.dataset.variantId);
        return;
      }

      const chip = event.target.closest('[data-cg-filter-remove]');
      if (chip) {
        this.uncheckFilter(chip.dataset.cgFilterRemove);
        return;
      }

      if (event.target.closest('[data-cg-filters-clear]')) {
        this.clearFilters();
        return;
      }

      const scrollBtn = event.target.closest('[data-cg-scroll]');
      if (scrollBtn) {
        this.scrollFilterRow(scrollBtn);
        return;
      }

      /* A radio can't be turned off by clicking it again, so a shopper who
         picked the wrong pill would be stuck with a filter on. Swallow the
         click on the active one and clear the row instead. */
      const filterLabel = event.target.closest('[data-cg-filter-option] .cg-filter');
      if (filterLabel) {
        const input = document.getElementById(filterLabel.htmlFor);
        if (input && input.checked) {
          event.preventDefault();
          input.checked = false;
          this.applyFilters();
        }
      }
    }

    handleChange(event) {
      if (event.target.closest('[data-cg-filter]')) {
        this.applyFilters();
        return;
      }

      const qtyInput = event.target.closest('[data-cg-qty]');
      if (qtyInput) {
        this.setQuantity(qtyInput.dataset.variantId, Number(qtyInput.value));
        return;
      }

      const variantInput = event.target.closest('.cg-modal__variant-input');
      if (!variantInput) return;

      const modal = variantInput.closest('.cg-modal');
      if (modal) this.loadBundleContents(modal);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      // A bundle item's drawer sits on top and closes itself first.
      if (document.querySelector('quick-view-modal[open]')) return;
      if (this.basket && !this.basket.hasAttribute('hidden')) {
        this.hideBasket();
      } else {
        this.hide();
      }
    }

    overlayIsLocked() {
      return Boolean(this.openModal) || (this.basket && !this.basket.hasAttribute('hidden'));
    }

    /* Scroll lock follows whichever overlay is actually open, so closing a
       product modal cannot leave overflow:hidden behind the basket (or vice versa). */
    syncOverlayLock() {
      const locked = this.overlayIsLocked();
      document.documentElement.classList.toggle('cg-modal-open', locked);
      if (locked) document.addEventListener('keydown', this.handleKeydown);
      else document.removeEventListener('keydown', this.handleKeydown);
    }

    /* Product detail modal */

    /* Templates stay out of the live document so Safari does not fetch every
       gallery on load. First open clones that gift into the page. */
    mountModal(modalId) {
      let modal = document.getElementById(modalId);
      if (modal) return modal;

      const tpl = this.querySelector('template[data-cg-modal="' + modalId + '"]');
      if (!tpl || !tpl.content) return null;

      const source = tpl.content.firstElementChild;
      if (!source) return null;
      modal = source.cloneNode(true);
      tpl.replaceWith(modal);
      return modal;
    }

    show(modalId, opener) {
      const modal = this.mountModal(modalId);
      if (!modal) return;

      this.hide();
      this.openModal = modal;
      this.opener = opener;
      modal.removeAttribute('hidden');
      this.syncOverlayLock();
      this.revealMainImage(modal);
      this.preloadGallery(modal);
      this.loadBundleContents(modal);
      this.restyleGallery(modal);

      const closeBtn = modal.querySelector('.cg-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    /* Gallery thumbs: hero first, then one restyled shot per item in the box
       (the same set the card collage shows), replacing the product's own
       contents photos. Runs once per modal; silently keeps the Liquid thumbs
       when the style is off or nothing resolves. */
    async restyleGallery(modal) {
      if (modal.dataset.cgGalleryStyled) return;
      const thumbs = modal.querySelector('.cg-modal__thumbs');
      const hero = thumbs && thumbs.querySelector('[data-cg-thumb]');
      if (!hero) return;

      let entries;
      try {
        const byHandle = await this.styledThumbs();
        if (!byHandle) return;
        entries = this.styledItems(await this.bundleItems(modal.dataset.cgVariantSku), byHandle);
      } catch (e) {
        return;
      }
      if (!entries.length || modal.dataset.cgGalleryStyled) return;
      modal.dataset.cgGalleryStyled = 'true';

      const template = hero.cloneNode(true);
      template.classList.remove('is-active');
      Array.from(thumbs.querySelectorAll('[data-cg-thumb]'))
        .slice(1)
        .forEach((thumb) => thumb.remove());

      entries.forEach((entry) => {
        const thumb = template.cloneNode(true);
        thumb.dataset.fullSrc = this.sizedThumb(entry.url, 900);
        thumb.setAttribute('aria-label', entry.title);
        const img = thumb.querySelector('img');
        if (img) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = this.sizedThumb(entry.url, 160);
          img.alt = entry.title;
        }
        thumbs.appendChild(thumb);
      });

      this.preloadGallery(modal);
    }

    /* The page shares one <bundle-contents> list: hand it to the modal being
       opened and tell it which variant to describe. Boxes with no bundle data
       hide the list themselves. */
    loadBundleContents(modal) {
      /* Re-resolve rather than trust the reference taken on connect: the list
         gets moved between modals, and a stale null here fails silently. */
      const list = this.bundleContents || this.querySelector('bundle-contents');
      this.bundleContents = list;

      const slot = modal.querySelector('[data-cg-bundle-slot]');
      if (!list || !slot) {
        console.warn('[bundle] no bundle list to fill', { list: !!list, slot: !!slot });
        return;
      }

      const previousSlot = list.parentElement;
      if (previousSlot !== slot) {
        slot.appendChild(list);
        if (previousSlot && previousSlot.hasAttribute('data-cg-bundle-slot')) {
          previousSlot.setAttribute('hidden', '');
          previousSlot.hidden = true;
        }
      }

      slot.setAttribute('hidden', '');
      slot.hidden = true;

      const checked = modal.querySelector('.cg-modal__variant-input:checked');
      const variantId = (checked && checked.value) || modal.dataset.cgVariantId;
      const sku = (checked ? checked.dataset.variantSku : modal.dataset.cgVariantSku) || '';
      if (!variantId) {
        console.warn('[bundle] no variant to describe', modal.id);
        return;
      }

      const run = () => {
        customElements.upgrade(list);
        if (typeof list.load !== 'function') {
          console.warn('[bundle] bundle-contents did not upgrade');
          return;
        }
        list.load(variantId, sku);
      };

      if (customElements.get('bundle-contents')) {
        run();
        return;
      }

      console.warn('[bundle] bundle-contents is not defined');
      customElements.whenDefined('bundle-contents').then(run);
    }

    /* Hidden + loading=lazy images often never start (Safari leaves the
       broken-image icon). Force the hero to load when the modal opens. */
    revealMainImage(modal) {
      const img = modal.querySelector('.cg-modal__main-image');
      if (!img) return;
      img.loading = 'eager';
      const src = img.getAttribute('src');
      if (src && !img.complete) img.src = src;
    }

    /* Fetch the full-size gallery images as soon as the modal opens, so
       switching thumbnails doesn't wait on a network request per click. */
    preloadGallery(modal) {
      this.preloadedImages = this.preloadedImages || new Set();

      modal.querySelectorAll('[data-cg-thumb]').forEach((thumb) => {
        const src = thumb.dataset.fullSrc;
        if (!src || this.preloadedImages.has(src)) return;

        this.preloadedImages.add(src);
        new Image().src = src;
      });
    }

    hide() {
      if (!this.openModal) return;

      this.openModal.setAttribute('hidden', '');
      if (this.opener) this.opener.focus();
      this.openModal = null;
      this.opener = null;
      this.syncOverlayLock();
    }

    selectThumb(thumb) {
      const modal = thumb.closest('.cg-modal');
      const mainImage = modal && modal.querySelector('.cg-modal__main-image');
      if (!mainImage || !thumb.dataset.fullSrc) return;

      mainImage.src = thumb.dataset.fullSrc;
      mainImage.removeAttribute('srcset');

      modal.querySelectorAll('[data-cg-thumb]').forEach((el) => {
        el.classList.toggle('is-active', el === thumb);
      });
    }

    /* Basket (client-side only, persisted in localStorage) */

    readBasket() {
      try {
        const items = JSON.parse(localStorage.getItem(BASKET_KEY));
        return Array.isArray(items) ? items : [];
      } catch (e) {
        return [];
      }
    }

    writeBasket(items) {
      try {
        localStorage.setItem(BASKET_KEY, JSON.stringify(items));
      } catch (e) {
        /* storage unavailable; basket lives in memory for the session */
      }
      this.renderBasket(items);
    }

    addToBasket(button) {
      let { variantId, variantTitle, price } = button.dataset;
      const modal = button.closest('.cg-modal');

      if (!variantId) {
        const checked = modal && modal.querySelector('.cg-modal__variant-input:checked');
        if (!checked) return;
        variantId = checked.value;
        variantTitle = checked.dataset.variantTitle;
        price = checked.dataset.price;
      }

      const items = this.readBasket();
      const alreadyAdded = items.some((item) => item.variantId === variantId);

      if (!alreadyAdded) {
        items.push({
          variantId: variantId,
          productId: button.dataset.productId,
          title: button.dataset.productTitle,
          variantTitle: variantTitle || '',
          price: Number(price),
          quantity: 1,
          image: this.itemImageUrl(button),
          url: button.dataset.productUrl || '',
        });
      }

      this.writeBasket(items);
      this.hide();
      this.showBasket();
    }

    removeItem(variantId) {
      this.writeBasket(this.readBasket().filter((item) => item.variantId !== variantId));
    }

    /* Quantity is per recipient; the order-details page multiplies it by the
       recipient count, so the cart only needs the per-box number. */
    changeQuantity(variantId, delta) {
      const item = this.readBasket().find((entry) => entry.variantId === variantId);
      if (item) this.setQuantity(variantId, (item.quantity || 1) + delta);
    }

    setQuantity(variantId, quantity) {
      quantity = Math.max(1, Math.floor(quantity) || 1);
      this.writeBasket(
        this.readBasket().map((item) => {
          if (item.variantId === variantId) item.quantity = quantity;
          return item;
        })
      );
    }

    buildStepper(item, quantity) {
      const stepper = document.createElement('div');
      stepper.className = 'cg-stepper';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'cg-stepper__btn';
      minus.textContent = '\u2212';
      minus.setAttribute('aria-label', this.t('decrease'));
      minus.dataset.cgStep = '-1';
      minus.dataset.variantId = item.variantId;
      minus.disabled = quantity <= 1;

      const value = document.createElement('input');
      value.type = 'number';
      value.className = 'cg-stepper__value';
      value.min = '1';
      value.inputMode = 'numeric';
      value.value = quantity;
      value.setAttribute('aria-label', this.t('quantity') + ' \u2014 ' + item.title);
      value.dataset.cgQty = '';
      value.dataset.variantId = item.variantId;

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'cg-stepper__btn';
      plus.textContent = '+';
      plus.setAttribute('aria-label', this.t('increase'));
      plus.dataset.cgStep = '1';
      plus.dataset.variantId = item.variantId;

      stepper.append(minus, value, plus);
      return stepper;
    }

    showBasket() {
      if (!this.basket) return;
      this.basket.removeAttribute('hidden');
      this.renderBasket();
      this.syncOverlayLock();

      const closeBtn = this.basket.querySelector('[data-cg-basket-close].cg-basket__close');
      if (closeBtn) closeBtn.focus();
    }

    hideBasket() {
      if (!this.basket) return;
      this.basket.setAttribute('hidden', '');
      this.syncOverlayLock();
    }

    /* Protocol-relative CDN URLs become http:// on theme-dev (127.0.0.1);
       Shopify's image CDN often will not serve that. */
    normalizeImageUrl(url) {
      if (!url || typeof url !== 'string') return '';
      let trimmed = url.trim();
      if (!trimmed || trimmed === '[object Object]') return '';
      trimmed = trimmed.replace(/&amp;/g, '&');
      if (trimmed.indexOf('//') === 0) return 'https:' + trimmed;
      return trimmed;
    }

    /* Prefer the gallery photo already on the page — same file the modal shows. */
    modalImageSrc(productId) {
      if (!productId) return '';
      const modal = this.querySelector('.cg-modal[data-cg-product-id="' + productId + '"]');
      const el = modal && modal.querySelector('.cg-modal__main-image');
      if (!el) return '';
      return this.normalizeImageUrl(el.getAttribute('src') || el.currentSrc || '');
    }

    itemImageUrl(button) {
      const modal = button.closest('.cg-modal');
      const mainImage = modal && modal.querySelector('.cg-modal__main-image');
      const live =
        mainImage && (mainImage.getAttribute('src') || mainImage.currentSrc || mainImage.src);
      return this.normalizeImageUrl(live || button.dataset.image || '');
    }

    appendBasketImage(row, item) {
      const src = this.modalImageSrc(item.productId) || this.normalizeImageUrl(item.image);
      if (!src) return;

      const img = document.createElement('img');
      img.className = 'cg-basket__item-image';
      img.alt = item.title || '';
      img.width = 64;
      img.height = 64;
      img.addEventListener('error', () => {
        const fallback = this.modalImageSrc(item.productId);
        if (fallback && img.dataset.retried !== 'true' && fallback !== img.getAttribute('src')) {
          img.dataset.retried = 'true';
          img.src = fallback;
          return;
        }
        img.remove();
      });
      img.src = src;
      row.appendChild(img);
    }

    /* Card pills mirror the cart: "+ Add" until the gift is in, then a
       coral "✓ In cart". Only single-variant pills carry a variant id. */
    syncCardPills(items) {
      if (!this.grid) return;
      const inCart = new Set(items.map((item) => String(item.variantId)));
      this.grid.querySelectorAll('[data-cg-card-add]').forEach((pill) => {
        const added = inCart.has(String(pill.dataset.variantId));
        pill.classList.toggle('is-added', added);
        const label = pill.querySelector('.cg-card__add-label');
        if (label) label.textContent = added ? pill.dataset.labelAdded : pill.dataset.labelAdd;
      });
    }

    renderBasket(items) {
      if (!this.basketItems) return;
      items = items || this.readBasket();
      this.syncCardPills(items);

      if (this.basketCount) this.basketCount.textContent = items.length;
      if (this.basketEmpty) this.basketEmpty.hidden = items.length > 0;
      if (this.basketFooter) this.basketFooter.hidden = items.length === 0;

      this.basketItems.textContent = '';

      items.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'cg-basket__item';

        if (item.image || item.productId) {
          this.appendBasketImage(row, item);
        }

        const info = document.createElement('div');
        info.className = 'cg-basket__item-info';

        const title = document.createElement('p');
        title.className = 'cg-basket__item-title';
        title.textContent = item.title;
        info.appendChild(title);

        const quantity = item.quantity || 1;
        if (item.variantTitle) {
          const variant = document.createElement('p');
          variant.className = 'cg-basket__item-variant';
          variant.textContent = item.variantTitle;
          info.appendChild(variant);
        }

        const controls = document.createElement('div');
        controls.className = 'cg-basket__item-controls';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cg-basket__remove';
        remove.textContent = this.t('remove');
        remove.dataset.cgRemove = '';
        remove.dataset.variantId = item.variantId;

        const hint = document.createElement('span');
        hint.className = 'cg-basket__item-hint';
        hint.textContent = this.t('perRecipient');

        controls.append(this.buildStepper(item, quantity), hint, remove);
        info.appendChild(controls);
        row.appendChild(info);

        const pricing = document.createElement('div');
        pricing.className = 'cg-basket__item-pricing';

        const linePrice = document.createElement('span');
        linePrice.className = 'cg-basket__item-price';
        linePrice.textContent = this.formatMoney(item.price * quantity);
        pricing.appendChild(linePrice);

        if (quantity > 1) {
          const each = document.createElement('span');
          each.className = 'cg-basket__item-each';
          each.textContent = quantity + ' \u00d7 ' + this.formatMoney(item.price);
          pricing.appendChild(each);
        }

        row.appendChild(pricing);

        this.basketItems.appendChild(row);
      });
    }

    t(key) {
      const fallbacks = {
        remove: 'Remove',
        quantity: 'Quantity',
        decrease: 'Decrease quantity',
        increase: 'Increase quantity',
        perRecipient: 'per recipient',
        productCountOne: '1 product found',
        productCountMany: '__COUNT__ products found',
        filtersRemove: 'Remove filter: __LABEL__',
        filtersUncovered:
          '__COUNT__ gifts are not matched by any “__GROUP__” filter, so shoppers can only reach them with that group cleared: __TITLES__.',
      };
      const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
      return this.dataset[attr] || fallbacks[key];
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const format = this.dataset.moneyFormat || '${{amount}}';
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    /* Sorting — corporate store lives on a page template (/pages/...), not a
       collection URL, so ?sort_by= reloads are ignored by Shopify. Reorder
       cards client-side instead. */

    featuredRank(el) {
      return Number(el.getAttribute('data-featured-rank')) || 99;
    }

    pinFeatured(compare) {
      return (a, b) => {
        const featuredDiff = this.featuredRank(a) - this.featuredRank(b);
        if (featuredDiff !== 0) return featuredDiff;
        return compare(a, b);
      };
    }

    getSortComparator(sortBy) {
      const comparators = {
        manual: (a, b) => Number(a.dataset.sortIndex) - Number(b.dataset.sortIndex),
        'title-ascending': (a, b) =>
          String(a.dataset.sortTitle || '').localeCompare(String(b.dataset.sortTitle || '')),
        'title-descending': (a, b) =>
          String(b.dataset.sortTitle || '').localeCompare(String(a.dataset.sortTitle || '')),
        'price-ascending': (a, b) => Number(a.dataset.sortPrice) - Number(b.dataset.sortPrice),
        'price-descending': (a, b) => Number(b.dataset.sortPrice) - Number(a.dataset.sortPrice),
        'created-ascending': (a, b) => Number(a.dataset.sortCreated) - Number(b.dataset.sortCreated),
        'created-descending': (a, b) => Number(b.dataset.sortCreated) - Number(a.dataset.sortCreated),
      };
      const compare = comparators[sortBy] || comparators.manual;
      if (!sortBy || sortBy === 'manual') return this.pinFeatured(compare);
      return compare;
    }

    sortCards(sortBy) {
      if (!this.grid) return;

      const cards = Array.from(this.grid.querySelectorAll('[data-cg-card]'));
      if (!cards.length) return;

      cards.sort(this.getSortComparator(sortBy));
      cards.forEach((card) => this.grid.appendChild(card));

      try {
        sessionStorage.setItem('cg-sort-by', sortBy || 'manual');
      } catch (e) {
        /* ignore */
      }
    }

    /* Filtering — client-side for the same reason as sorting. Every option sits
       in one row, but each keeps its `data-cg-group`, so groups still AND
       together (one active option per group, enforced by the radio name). */

    initFilters() {
      this.filterInputs = Array.from(this.querySelectorAll('[data-cg-filter]'));
      if (!this.filterInputs.length || !this.grid) return;

      this.filterPanel = this.querySelector('[data-cg-filters]');
      this.filterChips = this.querySelector('[data-cg-filters-chips]');
      this.filterActive = this.querySelector('[data-cg-filters-active]');
      this.filterEmpty = this.querySelector('[data-cg-filter-empty]');
      this.countEl = this.querySelector('[data-cg-count]');
      this.cards = Array.from(this.grid.querySelectorAll('[data-cg-card]'));

      this.pruneEmptyFilters();
      this.reportUncoveredProducts();
      this.restoreFilters();
      this.applyFilters();
      this.initFilterRows();
    }

    /* Each group scrolls sideways, so the arrows only appear on the side that
       still has pills left to reach. */
    initFilterRows() {
      this.filterRows = Array.from(this.querySelectorAll('[data-cg-scroller]'));
      if (!this.filterRows.length) return;

      this.updateFilterArrows = this.updateFilterArrows.bind(this);

      this.filterRows.forEach((row) => {
        const track = row.querySelector('[data-cg-scroll-track]');
        if (track) track.addEventListener('scroll', this.updateFilterArrows, { passive: true });
      });

      window.addEventListener('resize', this.updateFilterArrows);
      this.updateFilterArrows();
    }

    updateFilterArrows() {
      (this.filterRows || []).forEach((row) => {
        const track = row.querySelector('[data-cg-scroll-track]');
        const prev = row.querySelector('[data-cg-scroll="-1"]');
        const next = row.querySelector('[data-cg-scroll="1"]');
        if (!track) return;

        /* Sub-pixel track widths leave a pixel of scroll left over, which would
           strand the next arrow on a row that is already fully visible. */
        const remaining = track.scrollWidth - track.clientWidth - Math.ceil(track.scrollLeft);
        if (prev) prev.hidden = track.scrollLeft <= 1;
        if (next) next.hidden = remaining <= 1;
      });
    }

    scrollFilterRow(button) {
      const row = button.closest('[data-cg-scroller]');
      const track = row && row.querySelector('[data-cg-scroll-track]');
      if (!track) return;

      const direction = Number(button.dataset.cgScroll) || 1;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    }

    /* Theme editor only. A group built on title keywords can't know about a gift
       added later, so it would quietly leave that box unreachable — list the
       products no option in a group matches, while someone can still fix it.
       Only groups meant to cover the whole catalog opt in: a dietary group
       describing a handful of gifts is not a mistake. */
    reportUncoveredProducts() {
      const notice = this.querySelector('[data-cg-filter-notice]');
      if (!notice) return;

      const messages = [];

      this.filterInputsByGroup().forEach((inputs) => {
        const enabled = inputs.filter((input) => !input.disabled);
        if (!enabled.length || !enabled[0].hasAttribute('data-cg-warn-uncovered')) return;

        const matchers = enabled.map((input) => this.filterMatcher(input));
        const uncovered = this.cards.filter((card) => !matchers.some((matches) => matches(card)));
        if (!uncovered.length) return;

        const titles = uncovered.map((card) => {
          const titleEl = card.querySelector('.cg-card__title');
          return (titleEl ? titleEl.textContent : card.dataset.productHandle || '').trim();
        });

        messages.push(
          this.t('filtersUncovered')
            .replace('__COUNT__', String(uncovered.length))
            .replace('__GROUP__', enabled[0].dataset.cgGroupHeading || '')
            .replace('__TITLES__', titles.join(', '))
        );
      });

      notice.textContent = messages.join(' ');
      notice.hidden = messages.length === 0;
    }

    filterInputsByGroup() {
      return this.filterInputs.reduce((groups, input) => {
        const groupId = input.dataset.cgGroup || '';
        if (!groups.has(groupId)) groups.set(groupId, []);
        groups.get(groupId).push(input);
        return groups;
      }, new Map());
    }

    /* A facet the merchant configured ahead of the catalog would otherwise show
       a pill that empties the grid, so count each option against the products
       actually on the page and drop the ones nothing matches. */
    pruneEmptyFilters() {
      let anyVisible = false;

      this.filterInputs.forEach((input) => {
        const matches = this.filterMatcher(input);
        const count = this.cards.filter(matches).length;
        const option = input.closest('[data-cg-filter-option]');

        input.disabled = count === 0;
        if (option) option.hidden = count === 0;
        if (count > 0) anyVisible = true;
      });

      if (this.filterPanel) this.filterPanel.hidden = !anyVisible;
    }

    splitFilterValue(value) {
      return String(value || '')
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);
    }

    cardTags(card) {
      if (!card.cgTags) {
        card.cgTags = this.splitFilterValue(String(card.dataset.cgTags || '').toUpperCase());
      }
      return card.cgTags;
    }

    filterMatcher(input) {
      const tags = this.splitFilterValue(input.dataset.cgFilterTags);
      if (tags.length) {
        return (card) => {
          const cardTags = this.cardTags(card);
          return tags.some((tag) => cardTags.indexOf(tag) !== -1);
        };
      }

      const keywords = this.splitFilterValue(input.dataset.cgFilterKeywords).map((word) =>
        word.toLowerCase()
      );
      if (keywords.length) {
        return (card) => {
          const title = String(card.dataset.sortTitle || '').toLowerCase();
          return keywords.some((word) => title.indexOf(word) !== -1);
        };
      }

      const min = Number(input.dataset.cgFilterMin) || 0;
      const max = input.dataset.cgFilterMax ? Number(input.dataset.cgFilterMax) : Infinity;
      return (card) => {
        const price = Number(card.dataset.sortPrice) || 0;
        return price >= min && price <= max;
      };
    }

    checkedFilters() {
      return this.filterInputs.filter((input) => input.checked && !input.disabled);
    }

    applyFilters() {
      if (!this.filterInputs || !this.cards) return;

      const groups = new Map();
      this.checkedFilters().forEach((input) => {
        const groupId = input.dataset.cgGroup || '';
        if (!groups.has(groupId)) groups.set(groupId, []);
        groups.get(groupId).push(this.filterMatcher(input));
      });

      const groupMatchers = Array.from(groups.values());
      let visible = 0;

      this.cards.forEach((card) => {
        const match = groupMatchers.every((matchers) => matchers.some((matches) => matches(card)));
        card.hidden = !match;
        if (match) visible += 1;
      });

      this.renderFilterChips();
      this.updateProductCount(visible);
      if (this.filterEmpty) this.filterEmpty.hidden = visible > 0 || groupMatchers.length === 0;
      this.persistFilters();
    }

    renderFilterChips() {
      const active = this.checkedFilters();

      if (this.filterChips) {
        this.filterChips.textContent = '';

        active.forEach((input) => {
          const label = input.dataset.cgLabel || '';
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'cg-filter-chip';
          chip.dataset.cgFilterRemove = input.id;
          chip.setAttribute('aria-label', this.t('filtersRemove').replace('__LABEL__', label));
          chip.append(label);

          const cross = document.createElement('span');
          cross.className = 'cg-filter-chip__cross';
          cross.setAttribute('aria-hidden', 'true');
          cross.textContent = '\u00d7';
          chip.appendChild(cross);

          const item = document.createElement('li');
          item.appendChild(chip);
          this.filterChips.appendChild(item);
        });
      }

      if (this.filterActive) this.filterActive.hidden = active.length === 0;
    }

    updateProductCount(count) {
      if (!this.countEl) return;
      this.countEl.textContent =
        count === 1
          ? this.t('productCountOne')
          : this.t('productCountMany').replace('__COUNT__', String(count));
    }

    uncheckFilter(inputId) {
      const input = this.filterInputs.find((candidate) => candidate.id === inputId);
      if (!input) return;
      input.checked = false;
      this.applyFilters();
    }

    clearFilters() {
      this.checkedFilters().forEach((input) => {
        input.checked = false;
      });
      this.applyFilters();
    }

    /* Shoppers bounce to the order details page and back while building a
       basket, so keep their filters for the session the way sorting does. */
    persistFilters() {
      try {
        const ids = this.checkedFilters().map((input) => input.id);
        if (ids.length) {
          sessionStorage.setItem(FILTER_KEY, JSON.stringify(ids));
        } else {
          sessionStorage.removeItem(FILTER_KEY);
        }
      } catch (e) {
        /* ignore */
      }
    }

    restoreFilters() {
      let stored = null;
      try {
        stored = JSON.parse(sessionStorage.getItem(FILTER_KEY));
      } catch (e) {
        /* ignore */
      }
      if (!Array.isArray(stored) || !stored.length) return;

      this.filterInputs.forEach((input) => {
        if (!input.disabled && stored.indexOf(input.id) !== -1) input.checked = true;
      });
    }
  }

  window.wgSortCorporateGifts = function (select) {
    const root = select && select.closest && select.closest('corporate-gifts');
    if (!root || typeof root.sortCards !== 'function') return;
    root.sortCards(select.value);
  };

  customElements.define('corporate-gifts', CorporateGifts);
}
